import React, { useMemo, useState } from "react";
import { decisionGraphAttributeIds, findGraphNode, instanceIdentifier } from "./internal/graphLookup";
import InstanceIndex from "../../common/InstanceIndex/InstanceIndex";
import Tree, { type TreeItemProps } from "../../common/Tree/Tree";
import TreeItem from "../../common/Tree/TreeItem";
import AttributeName from "../AttributeName/AttributeName";
import AttributeValue from "../AttributeValue/AttributeValue";
import GraphProvider, { type ProvidedGraph } from "../GraphProvider/GraphProvider";
import NodePath from "../NodePath/NodePath";
import type { GraphDataTreeNodeData } from "./GraphData";
import styles from "./GraphData.module.scss";
import { mappingDataToTreeItem } from "./internal/mapping";

export interface GraphDataInlineTreeProps {
  graph: ProvidedGraph;
  attributeIds?: string[];
  showValues?: boolean;
  showValidationState?: boolean;
  preserveRoot?: boolean;
  childNodesLast?: boolean;
  rootPath?: string | null;
  className?: string;
}

const createFilteredGraph = (graph: ProvidedGraph, attributeIds: string[]) => {
  if (!graph) return null;
  const visible = new Uint8Array(graph.idBound());
  const nodeIds: string[] = [];
  for (const id of decisionGraphAttributeIds(graph, attributeIds)) {
    visible[id] = 1;
    nodeIds.push(graph.deref(id));
  }
  return { graph, visible, nodeIds };
};

const getCommonPrefix = (paths: string[][]) => {
  if (paths.length === 0) return [];
  const [firstPath, ...remainingPaths] = paths;
  const prefix: string[] = [];
  for (let index = 0; index < firstPath.length; index += 1) {
    const segment = firstPath[index];
    if (remainingPaths.every((path) => path[index] === segment)) {
      prefix.push(segment);
    } else {
      break;
    }
  }
  return prefix;
};

const getDisplayRootPath = (nodeIds: string[]) => {
  const parentPaths = nodeIds
    .map((nodeId) => nodeId.split("/").slice(0, -1))
    .filter((path) => path.length > 0);
  const commonParentPath = getCommonPrefix(parentPaths);

  if (commonParentPath.length >= 3) return commonParentPath.slice(0, 3).join("/");
  if (commonParentPath.length >= 1) return commonParentPath.slice(0, 1).join("/");
  return null;
};

const findTreeItem = (item: GraphDataTreeNodeData, id: string): GraphDataTreeNodeData | null => {
  if (item.id === id) return item;
  for (const child of item.children || []) {
    const found = findTreeItem(child, id);
    if (found) return found;
  }
  return null;
};

const getChildGroupOrder = (item: GraphDataTreeNodeData) => (item.info?.type === "attribute" ? 0 : 1);

const sortChildNodesLast = (item: GraphDataTreeNodeData): GraphDataTreeNodeData =>
  ({
    ...item,
    children: item.children
      ?.map((child, index) => ({ child, index }))
      .sort((left, right) => {
        const groupDiff = getChildGroupOrder(left.child) - getChildGroupOrder(right.child);
        return groupDiff === 0 ? left.index - right.index : groupDiff;
      })
      .map(({ child }) => sortChildNodesLast(child)),
  }) as GraphDataTreeNodeData;

const getNodeFromTreeItem = (graph: ProvidedGraph | undefined, item: any) => {
  const id = item?.info?.nodeId || item?.id;
  return id ? findGraphNode(graph, id) : null;
};

const InlineTreeItem = ({ node, ...props }: TreeItemProps) => {
  const info = (node?.data as any)?.info || {};
  const graph = (node?.data as any)?.graph;
  const showValues = (node?.data as any)?.showValues !== false;
  const rootPath = (node?.data as any)?.rootPath;
  const rootDescription = (node?.data as any)?.rootDescription;
  const nodeValue = info.type === "attribute" ? getNodeFromTreeItem(graph, node.data) : null;
  const graphNode = nodeValue && (node?.data as any)?.showValidationState === false
    ? { ...nodeValue, validation: undefined } : nodeValue;

  return (
    <TreeItem
      node={node}
      {...props}
      className={styles.treeItem}
    >
      <TreeItem.Arrow />
      {rootPath ? (
        <NodePath
          node={{ path: rootPath, description: rootDescription }}
          showDescription
          className={styles.inlineRootPath}
        />
      ) : info.type === "instance" ? (
        <div
          data-deci-part="graphdata-treeitem-instance-label"
          className={styles.treeInstanceLabel}
        >
          <InstanceIndex path={String(node.id)} />
        </div>
      ) : info.type === "attribute" && graphNode ? (
        <div
          data-deci-part="graphdata-inline-attribute"
          className={styles.inlineAttribute}
        >
          <AttributeName
            node={graphNode}
            className={styles.inlineAttributeName}
          />
          {showValues ? (
            <div className={styles.inlineAttributeValue}>
              <AttributeValue node={graphNode} />
            </div>
          ) : null}
        </div>
      ) : (
        <TreeItem.Label />
      )}
    </TreeItem>
  );
};

const attachGraph = (
  item: GraphDataTreeNodeData,
  graph: ProvidedGraph,
  showValues: boolean,
  showValidationState: boolean,
  rootPath?: string | null,
  rootDescription?: string,
): GraphDataTreeNodeData =>
  ({
    ...item,
    graph,
    showValues,
    showValidationState,
    rootPath,
    rootDescription,
    children: item.children?.map((child) => attachGraph(child, graph, showValues, showValidationState)),
  }) as GraphDataTreeNodeData;

const GraphDataInlineTree = (props: GraphDataInlineTreeProps) => {
  const {
    graph,
    attributeIds = [],
    showValues = true,
    showValidationState = true,
    preserveRoot = false,
    childNodesLast = false,
    rootPath = null,
    className,
  } = props;
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const root = useMemo(() => {
    const filteredGraph = createFilteredGraph(graph, attributeIds);
    if (!filteredGraph) return null;
    const tree = mappingDataToTreeItem(filteredGraph.graph, true, {}, filteredGraph.visible, showValidationState);
    if (!tree) return null;

    const displayRootPath = rootPath || (preserveRoot ? null : getDisplayRootPath(filteredGraph.nodeIds));
    const displayRoot = displayRootPath ? findTreeItem(tree, displayRootPath) || tree : tree;
    const orderedRoot = childNodesLast ? sortChildNodesLast(displayRoot) : displayRoot;
    const rootDescription =
      displayRootPath && graph
        ? (instanceIdentifier(graph, displayRootPath) as string | undefined) || displayRoot.info?.text
        : displayRoot.info?.text;

    return attachGraph(orderedRoot, graph, showValues, showValidationState, displayRootPath, rootDescription);
  }, [graph, attributeIds, showValues, showValidationState, preserveRoot, childNodesLast, rootPath]);

  if (!root) return null;

  return (
    <GraphProvider graph={graph}>
      <Tree
        data={[root]}
        selectedId={selectedId}
        onSelect={setSelectedId}
        openByDefault
        rowHeight={40}
        className={className}
      >
        {InlineTreeItem as any}
      </Tree>
    </GraphProvider>
  );
};

export default GraphDataInlineTree;
